import React, { useEffect, useMemo, useState } from "react";
import "./DateOrDaysPicker.scss";
import {
  RadioButton,
  TextInput,
  SelectInput,
  DateInput,
  type SelectOption,
} from "@SeemplicityDev/shared";
import { DateTime } from "luxon";

export const dateSelectionOptions = {
  DATE: "Date",
  DAYS: "Days",
} as const;

export const unitSelectionOptions = {
  DAYS: "days",
  MONTHS: "months",
} as const;

export type DateSelectionOption =
  (typeof dateSelectionOptions)[keyof typeof dateSelectionOptions];

export type UnitSelectionOption =
  (typeof unitSelectionOptions)[keyof typeof unitSelectionOptions];

export const calculateDifference = (
  value: string | number,
  unit: UnitSelectionOption
): number => {
  let numValue: number = value as number;

  if (isNaN(numValue)) {
    const givenDate = DateTime.fromISO(value as string);
    const today = DateTime.now();
    numValue = Math.abs(Math.floor(today.diff(givenDate, unit)?.[unit]));
  }

  return numValue;
};

// Mirrors @SeemplicityDev/shared DateInput's onChange event shape. The shared
// barrel exports the DateInput component but not its DateInputChangeEvent type,
// so we declare a structurally-compatible local alias to type the handler param.
interface DateInputChangeEvent {
  target: {
    name: string;
    value: string;
  };
}

export interface DateOrDaysPickerChange {
  mode: DateSelectionOption;
  value: string | number | null;
  unit: UnitSelectionOption;
}

export interface DateOrDaysPickerProps {
  onChange: (change: DateOrDaysPickerChange) => void;
  value?: string | number | null;
  savedSelectionType?: DateSelectionOption | null;
  savedUnit?: UnitSelectionOption | null;
  minDate?: string;
  maxYears?: number;
  setInvalid?: (invalid: boolean) => void;
  // Accepted but unused: the filter builder passes an onBlur handler at runtime;
  // the component never wired it up, so declaring it optional keeps the call
  // site type-safe without changing behavior.
  onBlur?: () => void;
  "data-testid"?: string;
}

const DateOrDaysPicker: React.FC<DateOrDaysPickerProps> = ({
  onChange,
  value,
  savedSelectionType,
  savedUnit,
  minDate,
  maxYears,
  setInvalid = () => {},
  "data-testid": dataTestId,
}) => {
  const [selectionOption, setSelectionOption] = useState<DateSelectionOption>(
    savedSelectionType || dateSelectionOptions.DATE
  );
  const [selectedValue, setSelectedValue] = useState<string | number | null>(
    savedSelectionType === dateSelectionOptions.DAYS
      ? calculateDifference(
          value as string | number,
          savedUnit as UnitSelectionOption
        )
      : (value ?? null)
  );
  const [selectedUnit, setSelectedUnit] = useState<UnitSelectionOption>(
    savedUnit || unitSelectionOptions.DAYS
  );
  const [hasNumOfDaysError, setHasNumOfDaysError] = useState(false);

  const currentMax = useMemo(() => {
    return !!maxYears
      ? selectedUnit === unitSelectionOptions.DAYS
        ? maxYears * 365
        : maxYears * 12
      : undefined;
  }, [selectedUnit, maxYears]);

  useEffect(() => {
    onChange({
      mode: selectionOption,
      value: selectedValue,
      unit: selectedUnit,
    });

    if (selectionOption === dateSelectionOptions.DAYS) {
      const hasErrors =
        !!currentMax &&
        selectionOption === dateSelectionOptions.DAYS &&
        currentMax < (selectedValue as number);
      setHasNumOfDaysError(hasErrors);
      setInvalid(hasErrors || !selectedValue);
    }
  }, [selectedValue, selectedUnit, selectionOption, currentMax]);

  const unitOptions = [
    { value: unitSelectionOptions.DAYS, label: "days" },
    { value: unitSelectionOptions.MONTHS, label: "months" },
  ];

  return (
    <div className={"date-or-days-picker"}>
      <RadioButton
        value={dateSelectionOptions.DATE}
        onChange={() => {
          setSelectionOption(dateSelectionOptions.DATE);
          setSelectedValue(null);
        }}
        checked={selectionOption === dateSelectionOptions.DATE}
        label={"On date"}
        data-testid={`${dataTestId}-date`}
      />
      <DateInput
        onChange={(e: DateInputChangeEvent) =>
          setSelectedValue(e.target?.value)
        }
        value={
          selectionOption === dateSelectionOptions.DATE
            ? (selectedValue as string | null)
            : ""
        }
        disabled={selectionOption !== dateSelectionOptions.DATE}
        noMargin
        minDate={minDate}
        data-testid={`${dataTestId}-date-input`}
      />
      <RadioButton
        value={dateSelectionOptions.DAYS}
        onChange={() => {
          setSelectionOption(dateSelectionOptions.DAYS);
          setSelectedValue(null);
        }}
        checked={selectionOption === dateSelectionOptions.DAYS}
        label={"Custom"}
        data-testid={`${dataTestId}-days`}
      />
      <div className={"number-and-time-unit-selection"}>
        <TextInput
          placeholder={"#"}
          type={"number"}
          disabled={selectionOption !== dateSelectionOptions.DAYS}
          value={
            selectionOption === dateSelectionOptions.DAYS
              ? (selectedValue as string | number | undefined)
              : ""
          }
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSelectedValue(e.target?.value)
          }
          error={
            hasNumOfDaysError && {
              message: `Max input is ${currentMax} ${selectedUnit}`,
            }
          }
          noMargin
          data-testid={`${dataTestId}-days-number`}
        />
        <SelectInput
          disabled={selectionOption !== dateSelectionOptions.DAYS}
          options={unitOptions}
          value={unitOptions.find((o) => o.value === selectedUnit)}
          onChange={(value: SelectOption | undefined) =>
            setSelectedUnit(value?.value)
          }
          noMargin
          data-testid={`${dataTestId}-days-unit-select`}
        />
      </div>
    </div>
  );
};

export default DateOrDaysPicker;
